import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fetchEnchargePeople } from "./encharge/api";
import type { EnchargeInstance, EnchargePerson } from "./encharge/types";
import { EmailListPersonDialog } from "./EmailListPersonDialog";
import { EnchargeTagsEditor } from "./EnchargeTagsEditor";

function CategoryIcons({
  categories,
}: {
  categories: Record<string, boolean>;
}) {
  return (
    <div className="space-y-1">
      {Object.entries(categories).map(([label, optedIn]) => (
        <div key={label} className="flex items-center gap-1.5 text-xs">
          {optedIn ? (
            <Check className="size-3.5 text-primary shrink-0" />
          ) : (
            <X className="size-3.5 text-muted-foreground/40 shrink-0" />
          )}
          <span className={optedIn ? "" : "text-muted-foreground"}>
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

export const EmailListsTable = ({ instance }: { instance: EnchargeInstance }) => {
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [allPeople, setAllPeople] = useState<EnchargePerson[]>([]);
  const [editPerson, setEditPerson] = useState<EnchargePerson | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
    setAllPeople([]);
  }, [instance, debouncedSearch, categoryFilter]);

  const { data, isPending, error, isFetching } = useQuery({
    queryKey: [
      "email-lists",
      "people",
      instance,
      page,
      debouncedSearch,
      categoryFilter,
    ],
    queryFn: () =>
      fetchEnchargePeople(instance, {
        page,
        search: debouncedSearch,
        category: categoryFilter,
      }),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!data?.people) return;
    setAllPeople((prev) => {
      const withTags = data.people.map((p) => ({
        ...p,
        tags: p.tags ?? [],
      }));
      if (page === 1) return withTags;
      const ids = new Set(prev.map((p) => p.id));
      const next = withTags.filter((p) => !ids.has(p.id));
      return [...prev, ...next];
    });
  }, [data?.people, page]);

  const categories = data?.categories ?? {};
  const hasMore = data?.pagination?.hasMore ?? false;
  const showCount = !error && allPeople.length > 0;

  const loadMore = useCallback(() => {
    if (!hasMore || isFetching) return;
    setPage((p) => p + 1);
  }, [hasMore, isFetching]);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const invalidate = () => {
    setPage(1);
    setAllPeople([]);
    queryClient.invalidateQueries({
      queryKey: ["email-lists", "people", instance],
    });
  };

  const updatePersonTags = (personId: string, tags: string[]) => {
    setAllPeople((prev) =>
      prev.map((p) => (p.id === personId ? { ...p, tags } : p)),
    );
    setEditPerson((current) =>
      current?.id === personId ? { ...current, tags } : current,
    );
  };

  const categoryOptions = Object.values(categories);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
          <Input
            placeholder={translate("crm.email_lists.search_placeholder", {
              _: "Search contacts...",
            })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
          <Select
            value={categoryFilter || "__all__"}
            onValueChange={(v) =>
              setCategoryFilter(v === "__all__" ? "" : v)
            }
          >
            <SelectTrigger className="w-full sm:w-56">
              <SelectValue
                placeholder={translate("crm.email_lists.all_categories", {
                  _: "All categories",
                })}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">
                {translate("crm.email_lists.all_categories", {
                  _: "All categories",
                })}
              </SelectItem>
              {categoryOptions.map((label) => (
                <SelectItem key={label} value={label}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          {translate("crm.email_lists.create_contact", {
            _: "Create contact",
          })}
        </Button>
      </div>

      {showCount ? (
        <p className="text-sm text-muted-foreground mb-4">
          {translate("crm.email_lists.showing_count", {
            count: String(allPeople.length),
            _: "%{count} contacts in list",
          })}
        </p>
      ) : null}

      {error ? (
        <p className="text-destructive text-sm mb-4">
          {error instanceof Error ? error.message : "Failed to load contacts"}
        </p>
      ) : null}

      {isPending && page === 1 ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Spinner className="size-4" />
          <span>{translate("crm.email_lists.loading", { _: "Loading..." })}</span>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {translate("crm.email_lists.fields.email", { _: "Email" })}
                </TableHead>
                <TableHead>
                  {translate("crm.email_lists.fields.first_name", {
                    _: "First name",
                  })}
                </TableHead>
                <TableHead>
                  {translate("crm.email_lists.fields.last_name", {
                    _: "Last name",
                  })}
                </TableHead>
                <TableHead>
                  {translate("crm.email_lists.fields.company", {
                    _: "Company",
                  })}
                </TableHead>
                <TableHead className="hidden lg:table-cell">
                  {translate("crm.email_lists.fields.postcode", {
                    _: "Postcode",
                  })}
                </TableHead>
                <TableHead className="hidden lg:table-cell">
                  {translate("crm.email_lists.fields.contact_person", {
                    _: "Gobo contact person",
                  })}
                </TableHead>
                <TableHead>
                  {translate("crm.email_lists.fields.tags", { _: "Tags" })}
                </TableHead>
                <TableHead>
                  {translate("crm.email_lists.fields.categories", {
                    _: "Categories",
                  })}
                </TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {allPeople.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center text-muted-foreground py-8"
                  >
                    {translate("crm.email_lists.empty", {
                      _: "No contacts found.",
                    })}
                  </TableCell>
                </TableRow>
              ) : (
                allPeople.map((person) => (
                  <TableRow key={person.id}>
                    <TableCell className="max-w-[180px] truncate">
                      {person.email}
                    </TableCell>
                    <TableCell>{person.firstName || "—"}</TableCell>
                    <TableCell>{person.lastName || "—"}</TableCell>
                    <TableCell className="max-w-[160px] truncate">
                      {person.company || "—"}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {person.postcode || "—"}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell max-w-[140px] truncate">
                      {person.contactPerson || "—"}
                    </TableCell>
                    <TableCell className="min-w-[140px] max-w-[220px]">
                      <EnchargeTagsEditor
                        instance={instance}
                        tags={person.tags ?? []}
                        personEmail={person.email}
                        personId={person.id}
                        persistImmediately
                        onChange={(nextTags) =>
                          updatePersonTags(person.id, nextTags)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <CategoryIcons
                        categories={person.communicationCategories}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setEditPerson(person)}
                      >
                        {translate("ra.action.edit", { _: "Edit" })}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {isFetching && page > 1 ? (
        <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground text-sm">
          <Spinner className="size-4" />
          <span>
            {translate("crm.email_lists.loading_more", {
              _: "Loading more...",
            })}
          </span>
        </div>
      ) : null}

      <div ref={loadMoreRef} className="h-1" />

      <EmailListPersonDialog
        mode="create"
        instance={instance}
        open={createOpen}
        onOpenChange={setCreateOpen}
        categories={categories}
        existingEmails={allPeople.map((p) => p.email)}
        onSaved={invalidate}
      />

      <EmailListPersonDialog
        mode="edit"
        instance={instance}
        open={!!editPerson}
        onOpenChange={(open) => {
          if (!open) setEditPerson(null);
        }}
        person={editPerson}
        categories={categories}
        onSaved={invalidate}
        onTagsChange={(tags) => {
          if (editPerson) updatePersonTags(editPerson.id, tags);
        }}
      />
    </div>
  );
};
